import { forwardRef } from "react";
import { useFormContext } from "react-hook-form";
import Root from "@shared/ui/FormField";
import { Input } from "@shared/ui/Input";
import { Textarea } from "@shared/ui/Textarea";
import { RadioGroup } from "@shared/ui/RadioGroup";
import { YesNoButtons } from "@shared/ui/YesNoButtons";
import { ForceYesInput } from "./ForceYesInput";
import { Select } from "@shared/ui/Select";
import { TimezoneSelect } from "@shared/ui/TimezoneSelect";
import { PhotoUploadInput } from "@shared/ui/PhotoUploadInput";
import { ResumeUploadInput } from "@shared/ui/ResumeUploadInput";
import { Checkbox } from "@shared/ui/Checkbox";
import { SelectChips } from "@shared/ui/SelectChips";
import { OrgExperienceInputs } from "@shared/ui/OrgExperienceInputs";
import { FieldType } from "./Form";
import { TypeVariant } from "@shared/ui/Type";
export interface Props extends FieldType {
  id: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  transformOnChange?: (val: string) => string;
  image?: string;
  disabled?: boolean;
  isCustomComponent?: boolean;
  labelVariant?: TypeVariant;
  className?: string;
  innerClassName?: string;
  isHideErrorMessage?: boolean;
  layout?: "vertical" | "horizontal";
  autoComplete?: "current-password" | "new-password";
}

export const FormField = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      id,
      label,
      icon,
      prefix,
      disabled = false,
      required,
      helpText,
      component,
      onChange,
      transformOnChange,
      isCustomComponent,
      image,
      labelVariant,
      className,
      innerClassName,
      isHideErrorMessage,
      layout = "vertical",
      autoComplete,
      ...props
    },
    ref
  ) => {
    const form = useFormContext();

    const { isLoading, isSubmitting, errors } = form.formState;

    const error = errors[name];

    const fieldComponents = {
      Checkbox: Checkbox,
      Input: Input,
      Textarea: Textarea,
      Select: Select,
      RadioGroup: RadioGroup,
      ForceYesInput: ForceYesInput,
      YesNoButtons: YesNoButtons,
      OrgExperienceInputs: OrgExperienceInputs,
      SelectChips: SelectChips,
      ResumeUploadInput: ResumeUploadInput,
      PhotoUploadInput: PhotoUploadInput,
      TimezoneSelect: TimezoneSelect,
    } as const;

    const Component =
      fieldComponents[component as keyof typeof fieldComponents];

    if (!Component) {
      throw new Error(`Invalid component type: ${component}`);
    }

    return (
      <Root
        id={name}
        label={label}
        error={error}
        icon={icon}
        helpText={helpText}
        prefix={prefix}
        className={className}
        innerClassName={innerClassName}
        isCustomComponent={isCustomComponent}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        required={required}
        image={image}
        layout={layout}
        labelVariant={labelVariant}
        isHideErrorMessage={isHideErrorMessage}
      >
        <Component
          name={name}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          error={!!error}
          required={required}
          isWithIcon={!!icon}
          disabled={disabled}
          id={name}
          onChange={onChange}
          transformOnChange={transformOnChange}
          prefix={prefix}
          autoComplete={autoComplete}
          {...props}
        />
      </Root>
    );
  }
);

FormField.displayName = "FormField";

export default FormField;
